import { type IstKonto, type IstLogic, type Model, BuMNF, GQL } from "@binale-tech/shared";
import { Category } from "@domain/models/Category";
import { Creditor } from "@domain/models/Creditor";

/** The shared IST rule (`BuMNF.getIstLogic`) reads FNR and BU scope as plain values; a Personenkonto has neither. */
const toIstKonto = (konto: Model.IExtNum | undefined): IstKonto | undefined => {
    if (!konto) {
        return undefined;
    }
    if (konto instanceof Category) {
        return { extNum: konto, fnr: konto.FNR, buScope: konto.buScope };
    }
    return { extNum: konto };
};

export class IstMNFUtils {
    /** `BuMNF.getIstLogic` for frontend accounts; categories must be enriched from the chart to carry FNR and BU scope. */
    static getLogic(
        productKey: GQL.IProductKey,
        yearConfig: Pick<GQL.ICompanyAccountingYear, "taxation" | "kontoExt" | "skr">,
        recordKonto: Model.IExtNum,
        itemKonto: Model.IExtNum,
        bu: Model.Bu,
    ): IstLogic {
        return BuMNF.getIstLogic(yearConfig, productKey, bu, toIstKonto(recordKonto), toIstKonto(itemKonto));
    }

    /**
     * Whether an FE settles a Personenkonto through an account that does not release Ist-taxed USt: IST year, one side
     * a Debitor or Kreditor (both carry the M-NF logic), the other a Sachkonto without FNR 10 and without a USt/VSt function (KU scope or none, e.g. 1370 or a
     * company-specific account not in the chart). The form then shows the
     * "kein Geldkonto" hint (PO, 2026-11-04); the converter books it with `skipTaxLegs`.
     * @param taxation - Taxation of the record's year
     * @param productKey - Product of the record
     * @param account - The account the hint would sit on
     * @param counterAccount - The other side of the booking
     */
    static isNonGeldkontoSettlement(
        taxation: string | undefined,
        productKey: GQL.IProductKey,
        account: Model.IExtNum | undefined,
        counterAccount: Model.IExtNum | undefined,
    ): boolean {
        if (taxation !== GQL.ICompanyTaxation.Ist || productKey !== GQL.IProductKey.Fe) {
            return false;
        }
        return (
            counterAccount instanceof Creditor &&
            account instanceof Category &&
            BuMNF.hasNoVatFunction(account.buScope) &&
            !BuMNF.isGeldkonto(account.FNR)
        );
    }
}
